import router from '#/router'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  getChargeProducts,
  getPhilippineCoinProducts,
  getVipGemChargeProducts,
  createOrder as requestCreateOrder,
  verifyReceipt,
  getUser,
  checkVipGemUser,
  getChargeProductIten,
} from "#/api/index.js"
import { toastError } from "#/utils/toast"
import { loadScript } from "@paypal/paypal-js"
import * as Airwallex from '@airwallex/components-sdk'
import { isPhilippineRechargeUser, filterCoinProducts, buildPhilippineCoinOrder, coinPaymentUrl } from '#/utils/coinRecharge.js'
import { createRechargeResultStore, isRechargeCallback, rechargeReceiptRequest, rechargeDisplayResult, verifyRechargeOnce } from '#/utils/rechargeResult.js'

// Coin 和 Gem 支付回跳后均进行一次服务端验单，并展示结果弹窗。
const RECEIPT_VERIFICATION_ENABLED = true
const VIP_GEM_PENDING_ORDER_KEY = '__oumi.vip_gem_pending_order'
const RECHARGE_RESULT_CONTEXT_KEY = '__oumi.recharge_result_context'


export function useOumiRecharge() {
  const { t } = useI18n()

  const loading = ref(false)
  const select_recharge_id = ref(1)
  const select_recharge_item = ref()
  const paypal_product_item = ref()
  const recharge_list = ref([])
  const supportedPayTypes = ref([])
  const user = ref(null)
  const user_id = ref('')
  const isNewUser = ref(false)
  const clickPayItem = ref(null)
  const activeRechargeType = ref('coins')
  const gemRechargeAllowed = ref(false)
  const gemProductsLoading = ref(false)
  const gemBaseProducts = ref([])
  const gemUserIdentity = ref('')
  const gemVipPayTypes = ref([])
  const validatedGemUid = ref('')
  const coinProductList = ref([])
  const coinPayTypes = ref([])
  const coinProductsLoading = ref(false)
  const coinCatalogUid = ref('')
  const validatedCoinUid = ref('')
  const isPhilippineCoinUser = computed(() => activeRechargeType.value === 'coins' && isPhilippineRechargeUser(user_id.value))
  const canSubmitPhilippineCoins = computed(() => isPhilippineCoinUser.value
    && !!user.value && validatedCoinUid.value === `${user_id.value}`
    && coinCatalogUid.value === `${user_id.value}` && !coinProductsLoading.value
    && !!select_recharge_item.value?.supportedPayType)
  const rechargeResult = ref(null)
  const rechargeOptionsLoading = computed(() => (
    activeRechargeType.value === 'gems' ? gemProductsLoading.value : coinProductsLoading.value
  ))
  const showRechargeOptions = computed(() => (
    activeRechargeType.value === 'coins'
    || gemProductsLoading.value
    || recharge_list.value.length > 0
  ))
  const showPaymentOptions = computed(() => (
    activeRechargeType.value === 'coins' || gemRechargeAllowed.value
  ))
  const isGuildGemUser = computed(() => (
    activeRechargeType.value === 'gems'
    && gemRechargeAllowed.value
    && gemUserIdentity.value === 'GUILD_USER'
  ))
  const isTest = import.meta.env.VITE_NODE_ENV !== 'production'

  var chargeUid = 0
  let orderMap = {}
  let gemBaseProductsPromise = null
  let coinProductGeneration = 0
  let coinUserGeneration = 0

  const resultStore = createRechargeResultStore(sessionStorage, localStorage)
  let callbackContext = null

  function showRechargeResult(status, rechargeType, message = '', uid = user_id.value) {
    const context = callbackContext?.rechargeType === rechargeType
      && `${callbackContext.userId}` === `${uid}`
      ? (resultStore.get(uid, callbackContext.orderId) || callbackContext)
      : {}
    const account = `${user.value?.userId}` === `${uid}` ? user.value : {}
    rechargeResult.value = {
      status: status === 'success' ? 'success' : 'failed',
      rechargeType,
      message,
      orderId: context.orderId || '',
      userId: `${uid || ''}`,
      avatar: context.avatar || account?.avatar || '',
      nickname: context.nickname || account?.nickName || '',
      amount: context.amount ?? '',
    }
    // 弹窗显示时保留数据；切换响应式组件或刷新时仍能恢复，关闭才清除。
    resultStore.setActive(rechargeResult.value)
  }

  function closeRechargeResult() {
    rechargeResult.value = null
    resultStore.clearActive()
  }

  async function clearRechargeCallback(uid) {
    await router.replace({
      path: '/recharge',
      query: uid ? { uid: `${uid}` } : {},
    })
  }

  async function createOrder(data) {
    const rechargeType = data.scene === 4 ? 'gems' : 'coins'
    const product = `${select_recharge_item.value?.id}` === `${data.product_id}`
      ? select_recharge_item.value
      : recharge_list.value.find(item => `${item.id}` === `${data.product_id}`)
    // 在异步请求前固定本次订单的档位、币种和账号，不能用回跳后的默认档位替代。
    const context = {
      rechargeType,
      userId: `${data.user_id}`,
      productId: data.product_id,
      amount: (rechargeType === 'gems' ? product?.gems : product?.coin) ?? '',
      avatar: user.value?.avatar || '',
      nickname: user.value?.nickName || '',
    }
    resultStore.clearActive()
    const response = await requestCreateOrder(data)
    if (response?.success) {
      const order = response.data || {}
      const info = order.payInfo ?? order.pay_info ?? {}
      const extra = info.payInfoExtra ?? info.pay_info_extra ?? {}
      const payment = parseJsonValue(extra.payExtra ?? extra.pay_extra) || {}
      const secret = payment.clientSecret ?? payment.client_secret ?? ''
      const stripeSessionId = payment.sessionId ?? payment.session_id
        ?? (/^cs_/.test(secret) ? secret.split('_secret_')[0] : '')
      const saved = {
        ...context,
        orderId: order.orderId ?? order.order_id,
        paymentId: info.id,
        paypalOrderId: data.pay_item_type === 'PAYPAL' ? payment.id : '',
        stripeSessionId,
        result: null,
        checkedAt: 0,
      }
      resultStore.save(saved)
      sessionStorage.setItem(RECHARGE_RESULT_CONTEXT_KEY, JSON.stringify(saved))
    }
    return response
  }

  // 判断支付方式是否可用
  function isPaymentMethodAvailable(payType) {
    if (!select_recharge_item.value) return false
    if (activeRechargeType.value === 'coins'
      && (isPhilippineCoinUser.value || coinProductsLoading.value || coinCatalogUid.value !== `${user_id.value}`)) return false

    if (activeRechargeType.value === 'gems') {
      return gemRechargeAllowed.value
        && supportedPayTypes.value.includes(normalizePayType(payType))
    }
    
    // 获取实际美元价格
    const priceInDollars = select_recharge_item.value.coin / 50
    
    // Airwallex 需要50美元起步
    if (payType === 'AIRWALLEX') {
      return priceInDollars >= 50
    }
    
    // 其他支付方式20美元起步
    return priceInDollars >= 20
  }

  function parsePrice(value) {
    if (typeof value === 'number') return value
    const normalized = `${value ?? ''}`.replace(/,/g, '')
    const match = normalized.match(/\d+(?:\.\d+)?/)
    return match ? Number(match[0]) : NaN
  }

  function getPaymentPrice(item) {
    const raw = item?.localPrice ?? item?.local_price ?? item?.cent
    const amount = parsePrice(raw)
    if (!Number.isFinite(amount)) return ''
    const formatted = amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    // 保留接口中的货币标识；仅有数字时沿用美元显示。
    return typeof raw === 'string' && /[^\d,.\s]/.test(raw)
      ? raw.replace(/\d[\d,]*(?:\.\d+)?/, formatted)
      : `$${formatted}`
  }

  function getPaymentDiscount(payType) {
    if (activeRechargeType.value === 'gems') return ''
    if (!select_recharge_item.value) return ''

    const paymentProduct = payType === 'PAYPAL'
      ? paypal_product_item.value
      : select_recharge_item.value
    const currentPrice = parsePrice(
      paymentProduct?.localPrice
      ?? paymentProduct?.local_price
      ?? paymentProduct?.cent
    )
    const originalPrice = Number(select_recharge_item.value.coin) / 50

    if (
      !Number.isFinite(currentPrice)
      || !Number.isFinite(originalPrice)
      || currentPrice <= 0
      || originalPrice <= 0
      || currentPrice >= originalPrice
    ) {
      return ''
    }

    const discount = Math.round((1 - currentPrice / originalPrice) * 1000) / 10
    const discountText = Number.isInteger(discount) ? discount.toFixed(0) : discount.toFixed(1)
    return `${discountText}% OFF`
  }

  function getCoinOriginalPrice(item = select_recharge_item.value) {
    const coin = Number(item?.coin)
    return Number.isFinite(coin) && coin > 0
      ? `($${(coin / 50).toFixed(2)})`
      : ''
  }

  function normalizePayType(payType) {
    return `${payType || ''}`.trim().toUpperCase()
  }

  function getRechargeAmount(item) {
    return activeRechargeType.value === 'gems' ? item?.gems : item?.coin
  }

  function getRechargePrice(item) {
    if (activeRechargeType.value === 'gems') {
      return formatGemPrice(item)
    }
    return `$${(Number(item?.coin || 0) / 50).toFixed(2)}`
  }

  async function airwallexPayClick() {
    if (!user.value) {
      toastError(t('recharge.errors.enterUserToRecharge'))
      return 0;
    }

    if (clickPayItem.value) {
      toastError(t('recharge.errors.pleaseWait'))
      return;
    }

    try {
      clickPayItem.value = "AIRWALLEX"

      var requestBody = {
        product_id: select_recharge_id.value,
        pay_item_type: "AIRWALLEX",
        user_id: user.value.userId,
        v2: true,
        extra: JSON.stringify({
          return_url: window.location.origin + '/recharge'
        })
      }
      loading.value = true

      const rsp = await createOrder(requestBody)
      
      var airwallexInfo = JSON.parse(rsp.data.payInfo.payInfoExtra.payExtra)
      console.log(airwallexInfo)
      const { payments } = await Airwallex.init({
          env: isTest ? 'demo' : 'prod', // Setup which Airwallex env('staging' | 'demo' | 'prod') to integrate with
          enabledElements: ['payments']})
      
      payments.redirectToCheckout({
        env: isTest ? 'demo' : 'prod',
        mode: 'payment',
        currency: 'USD', 
        intent_id: airwallexInfo.intentId,
        client_secret: airwallexInfo.client_secret,
        successUrl: window.location.origin + `/recharge?uid=${user.value.userId}&airwallex=1&orderId=${rsp.data.orderId}`,
        failUrl: window.location.origin + '/recharge?airwallex=1',
        appearance: {
              mode: 'dark', // choose between 'dark' and 'light'
              variables: {
                colorBrand: '#612FFF',
              },
            }
      })

      loading.value = false
      clickPayItem.value = null

    } catch (error) {
      toastError(t('recharge.errors.topUpFailed'))
    } finally {
      loading.value = false
      clickPayItem.value = null
    }
  }

  function priceChangeHandle(item) {
    if (activeRechargeType.value === 'gems' && !gemRechargeAllowed.value) return

    select_recharge_id.value = item.id
    select_recharge_item.value = item
    if (activeRechargeType.value === 'gems') {
      supportedPayTypes.value = gemUserIdentity.value === 'GUILD_USER'
        ? [normalizePayType(item.supportedPayType)].filter(Boolean)
        : [...gemVipPayTypes.value]
      paypal_product_item.value = null
    } else {
      if (!isPhilippineCoinUser.value) loadPaypalProductItem()
    }
  }

  async function loadPaypalProductItem() {
    if (isPhilippineCoinUser.value) return
    const uid = `${user_id.value}`
    const id = select_recharge_id.value
    let res = await getChargeProductIten(id, "PAYPAL", uid)
    if (res && res.success && uid === `${user_id.value}` && id === select_recharge_id.value && activeRechargeType.value === 'coins') {
      paypal_product_item.value = res.data
    }
  }

  async function getProductList() {
    const current = ++coinProductGeneration
    const uid = `${user_id.value ?? ''}`
    const philippine = isPhilippineRechargeUser(uid)
    coinProductsLoading.value = true
    coinCatalogUid.value = ''
    coinProductList.value = []
    coinPayTypes.value = []
    if (activeRechargeType.value === 'coins') {
      recharge_list.value = []
      select_recharge_item.value = null
      paypal_product_item.value = null
      supportedPayTypes.value = []
    }
    try {
      const res = await (philippine ? getPhilippineCoinProducts(uid) : getChargeProducts(uid))
      if (current !== coinProductGeneration || uid !== `${user_id.value ?? ''}`) return
      if (!res?.success) throw new Error(res?.message || t('recharge.errors.topUpFailed'))
      coinProductList.value = filterCoinProducts(res.data?.product, philippine)
      coinPayTypes.value = philippine ? [] : (res.data?.supportedPayTypes ?? [])
      coinCatalogUid.value = uid
      if (activeRechargeType.value !== 'coins') return

      recharge_list.value = [...coinProductList.value]
      supportedPayTypes.value = [...coinPayTypes.value]
      if (coinProductList.value.length > 0) {
        select_recharge_id.value = coinProductList.value[0].id
        select_recharge_item.value = coinProductList.value[0]
        if (!philippine) loadPaypalProductItem()
      }
    } catch (error) {
      if (current === coinProductGeneration && uid === `${user_id.value ?? ''}`)
        toastError(error?.message || t('recharge.errors.topUpFailed'))
    } finally {
      if (current === coinProductGeneration) coinProductsLoading.value = false
    }
  }

  async function philippineCoinTopUpClick() {
    if (!canSubmitPhilippineCoins.value || clickPayItem.value) return
    const product = select_recharge_item.value
    clickPayItem.value = product.supportedPayType
    loading.value = true
    try {
      const requestBody = buildPhilippineCoinOrder(product, user.value.userId, `${window.location.origin}/recharge`)
      const rsp = await createOrder(requestBody)
      const url = rsp?.success && coinPaymentUrl(rsp.data)
      if (!url) throw new Error(rsp?.message || t('recharge.errors.createOrderFailed'))
      window.location.assign(url)
    } catch (error) {
      toastError(error?.response?.data?.message ?? error?.message ?? t('recharge.errors.topUpFailed'))
    } finally {
      clickPayItem.value = null
      loading.value = false
    }
  }

  function normalizeGemProducts(products) {
    return products.map(item => ({
      ...item,
      id: item.id,
      gems: item.gems ?? item.coin,
      supportedPayType: normalizePayType(
        item.supportedPayType ?? item.supported_pay_type
      ),
      localPrice: formatGemPrice(item),
    }))
  }

  function formatGemPrice(item) {
    return getPaymentPrice({ cent: item?.cent })
  }

  function applyGemProducts(products, selectFirst = false) {
    recharge_list.value = [...products]
    supportedPayTypes.value = []
    select_recharge_id.value = null
    select_recharge_item.value = null

    if (selectFirst && products.length > 0) {
      priceChangeHandle(products[0])
    }
  }

  async function loadBaseGemProducts() {
    gemProductsLoading.value = true
    try {
      const res = await getVipGemChargeProducts()
      if (!res?.success || !Array.isArray(res.data?.product)) {
        throw new Error(res?.message || 'Unable to load Gem top-up options.')
      }

      gemBaseProducts.value = normalizeGemProducts(res.data.product)
      if (activeRechargeType.value === 'gems' && !gemRechargeAllowed.value) {
        applyGemProducts(gemBaseProducts.value)
      }
      return gemBaseProducts.value
    } catch (error) {
      gemBaseProducts.value = []
      if (activeRechargeType.value === 'gems') {
        applyGemProducts([])
        toastError(error?.response?.data?.message ?? error?.message ?? 'Unable to load Gem top-up options.')
      }
      return []
    } finally {
      gemProductsLoading.value = false
    }
  }

  function ensureBaseGemProducts() {
    if (gemBaseProducts.value.length > 0) {
      return Promise.resolve(gemBaseProducts.value)
    }
    if (!gemBaseProductsPromise) {
      gemBaseProductsPromise = loadBaseGemProducts().finally(() => {
        gemBaseProductsPromise = null
      })
    }
    return gemBaseProductsPromise
  }

  async function loadGuildGemProducts(uid) {
    gemProductsLoading.value = true
    try {
      const res = await getVipGemChargeProducts(uid)
      if (!res?.success || !Array.isArray(res.data?.product) || res.data.product.length === 0) {
        throw new Error(res?.message || 'Unable to load Gem top-up options.')
      }
      return normalizeGemProducts(res.data.product)
    } finally {
      gemProductsLoading.value = false
    }
  }

  function resetGemEligibility() {
    gemRechargeAllowed.value = false
    gemUserIdentity.value = ''
    gemVipPayTypes.value = []
    validatedGemUid.value = ''
    supportedPayTypes.value = []
    select_recharge_item.value = null
    select_recharge_id.value = null
    if (activeRechargeType.value === 'gems') {
      applyGemProducts(gemBaseProducts.value)
    }
  }

  async function validateGemUser() {
    resetGemEligibility()

    if (!user_id.value) {
      user.value = null
      return
    }

    const validatingUid = `${user_id.value}`

    try {
      await ensureBaseGemProducts()
      if (
        activeRechargeType.value !== 'gems'
        || `${user_id.value}` !== validatingUid
      ) return

      const res = await checkVipGemUser({ user_id: Number(validatingUid) })
      if (
        activeRechargeType.value !== 'gems'
        || `${user_id.value}` !== validatingUid
      ) {
        return
      }
      if (!res?.success) {
        user.value = null
        toastError(res?.message ?? t('recharge.errors.enterUserToRecharge'))
        return
      }

      const data = res.data || {}
      const identity = `${data.userIdentity ?? data.user_identity ?? ''}`.toUpperCase()
      if (!['VIP_USER', 'GUILD_USER'].includes(identity)) {
        throw new Error(res?.message || 'This user is not eligible for Gem top-up.')
      }

      user.value = {
        ...data,
        userId: data.userId ?? data.user_id ?? validatingUid,
        nickName: data.nickName ?? data.nick_name ?? '',
        avatar: data.avatar ?? '',
      }
      isNewUser.value = false
      gemUserIdentity.value = identity
      validatedGemUid.value = validatingUid

      if (identity === 'GUILD_USER') {
        const guildProducts = await loadGuildGemProducts(validatingUid)
        if (
          activeRechargeType.value !== 'gems'
          || `${user_id.value}` !== validatingUid
        ) return

        gemRechargeAllowed.value = true
        applyGemProducts(guildProducts, true)
        return
      }

      gemVipPayTypes.value = (
        data.supportedPayTypes ?? data.supported_pay_types ?? []
      ).map(normalizePayType).filter(Boolean)
      if (gemBaseProducts.value.length === 0) {
        await ensureBaseGemProducts()
      }
      if (gemVipPayTypes.value.length === 0 || gemBaseProducts.value.length === 0) {
        throw new Error('No payment method is available for this user.')
      }

      gemRechargeAllowed.value = true
      applyGemProducts(gemBaseProducts.value, true)
    } catch (error) {
      if (
        activeRechargeType.value !== 'gems'
        || `${user_id.value}` !== validatingUid
      ) {
        return
      }
      user.value = null
      resetGemEligibility()
      toastError(error?.response?.data?.message ?? error?.message ?? t('recharge.errors.enterUserToRecharge'))
    }
  }

  async function switchRechargeType(type) {
    if (!['coins', 'gems'].includes(type) || type === activeRechargeType.value) return

    activeRechargeType.value = type
    user.value = null
    isNewUser.value = false
    paypal_product_item.value = null

    if (type === 'gems') {
      await ensureBaseGemProducts()
      if (activeRechargeType.value !== 'gems') return
      applyGemProducts(gemBaseProducts.value)
      if (user_id.value) {
        await validateGemUser()
      }
      return
    }

    gemRechargeAllowed.value = false
    await getProductList()
    if (user_id.value) {
      await getinfoByUid(user_id.value)
    }
  }

  function checkUid(e) {
    let str = e.target.value;
    if (str[0] == 0) {
      user_id.value = str.substr(1);
    }
    user_id.value = user_id.value + '';
    user_id.value = user_id.value.replace(/[^\d]/g, '')
    if (activeRechargeType.value === 'coins' && `${user_id.value}` !== validatedCoinUid.value) {
      user.value = null
      isNewUser.value = false
    }
    if (
      activeRechargeType.value === 'gems'
      && `${user_id.value}` !== `${validatedGemUid.value}`
    ) {
      user.value = null
      resetGemEligibility()
    }
  }

  async function saveUid(e) {
    const inputValue = `${e?.target?.value ?? user_id.value ?? ''}`.replace(/[^\d]/g, '')
    user_id.value = inputValue

    if (activeRechargeType.value === 'gems') {
      await validateGemUser()
      return
    }

    if (inputValue.length > 0) {
      await Promise.all([getinfoByUid(inputValue), getProductList()])
    } else {
      coinUserGeneration++
      validatedCoinUid.value = ''
      user.value = null
      isNewUser.value = false
      await getProductList()
    }
  }

  async function clearUserInput() {
    coinUserGeneration++
    validatedCoinUid.value = ''
    user_id.value = ''
    user.value = null
    isNewUser.value = false
    resetGemEligibility()
    if (activeRechargeType.value === 'coins') {
      await getProductList()
    }
  }

  async function getinfoByUid(uid) {
    const current = ++coinUserGeneration
    validatedCoinUid.value = ''
    user.value = null
    isNewUser.value = false
    chargeUid = uid
    if (chargeUid) {
      try {
        const res = await getUser({ user_id: uid })
        if (current !== coinUserGeneration || `${uid}` !== `${user_id.value}` || activeRechargeType.value !== 'coins') return
        if (!res?.success) throw new Error(res?.message || t('recharge.errors.enterUserToRecharge'))
        user.value = res.data
        validatedCoinUid.value = `${uid}`
        isNewUser.value = Boolean(res.data?.isNewUser ?? res.isNewUser)
      } catch (error) {
        if (current === coinUserGeneration && `${uid}` === `${user_id.value}`)
          toastError(error?.message || t('recharge.errors.enterUserToRecharge'))
      }
    }
  }

  function getGemPayType(prefix) {
    const normalizedPrefix = normalizePayType(prefix)
    return supportedPayTypes.value.find(payType => (
      normalizePayType(payType).startsWith(normalizedPrefix)
    )) || normalizedPrefix
  }

  function parseJsonValue(value) {
    if (typeof value !== 'string') return value
    try {
      return JSON.parse(value)
    } catch (error) {
      return value
    }
  }

  function normalizeCallbackQuery(query) {
    const normalizedQuery = { ...query }
    const rawUid = Array.isArray(normalizedQuery.uid)
      ? normalizedQuery.uid[0]
      : normalizedQuery.uid
    const separatorIndex = `${rawUid ?? ''}`.indexOf('?')

    if (separatorIndex >= 0) {
      normalizedQuery.uid = `${rawUid}`.slice(0, separatorIndex)
      const appendedQuery = new URLSearchParams(
        `${rawUid}`.slice(separatorIndex + 1)
      )
      appendedQuery.forEach((value, key) => {
        if (normalizedQuery[key] === undefined) {
          normalizedQuery[key] = value
        }
      })
    }

    return normalizedQuery
  }

  function findPaymentUrl(value, depth = 0) {
    if (!value || depth > 4) return ''
    if (typeof value === 'string') {
      if (/^https?:\/\//i.test(value)) return value
      const parsed = parseJsonValue(value)
      return parsed === value ? '' : findPaymentUrl(parsed, depth + 1)
    }
    if (typeof value !== 'object') return ''

    const urlKeys = [
      'redirectUrl',
      'redirect_url',
      'paymentUrl',
      'payment_url',
      'payUrl',
      'pay_url',
      'checkoutUrl',
      'checkout_url',
      'url',
    ]
    for (const key of urlKeys) {
      const url = findPaymentUrl(value[key], depth + 1)
      if (url) return url
    }

    for (const nestedValue of Object.values(value)) {
      const url = findPaymentUrl(nestedValue, depth + 1)
      if (url) return url
    }
    return ''
  }

  async function createVipGemOrder(payType) {
    if (!gemRechargeAllowed.value || !user.value || !select_recharge_item.value) {
      toastError(t('recharge.errors.enterUserToRecharge'))
      return
    }
    if (clickPayItem.value) {
      toastError(t('recharge.errors.pleaseWait'))
      return
    }

    const normalizedPayType = normalizePayType(payType)
    const uid = user.value.userId ?? user_id.value
    const gemResultUrl = `${window.location.origin}/recharge?gem_result=1&uid=${encodeURIComponent(uid)}`
    // Stripe 服务端会在 return_url 后追加 ?session_id=...，因此不能预先携带 query。
    // Gem 身份和订单信息通过 sessionStorage 中的 pending order 恢复。
    const returnUrl = normalizedPayType === 'STRIPE'
      ? `${window.location.origin}/recharge`
      : gemResultUrl
    clickPayItem.value = normalizedPayType
    loading.value = true

    try {
      const rsp = await createOrder({
        product_id: select_recharge_id.value,
        pay_item_type: normalizedPayType,
        user_id: uid,
        scene: 4,
        v2: true,
        extra: JSON.stringify({
          return_url: returnUrl,
          front_callback_url: gemResultUrl,
          callback_url: gemResultUrl,
          cancel_url: `${window.location.origin}/recharge?uid=${encodeURIComponent(uid)}`,
        }),
      })
      if (!rsp?.success) {
        toastError(rsp?.message ?? t('recharge.errors.createOrderFailed'))
        return
      }

      const orderData = rsp.data || {}
      const orderId = orderData.orderId ?? orderData.order_id
      const payInfo = orderData.payInfo ?? orderData.pay_info ?? {}
      const payInfoExtra = payInfo.payInfoExtra ?? payInfo.pay_info_extra ?? {}
      const action = normalizePayType(payInfoExtra.action)
      const payExtra = payInfoExtra.payExtra ?? payInfoExtra.pay_extra
      const parsedPayExtra = parseJsonValue(payExtra)

      sessionStorage.setItem(VIP_GEM_PENDING_ORDER_KEY, JSON.stringify({
        orderId,
        userId: uid,
      }))

      // Stripe 的 WEB 动作返回的是 Embedded Checkout 参数，而不是跳转 URL。
      // 因此以 clientSecret 是否存在作为 Stripe Web Checkout 的调起依据。
      if (normalizedPayType === 'STRIPE') {
        const stripeInfo = typeof parsedPayExtra === 'object'
          ? {
              ...parsedPayExtra,
              clientSecret: parsedPayExtra.clientSecret ?? parsedPayExtra.client_secret,
            }
          : null
        if (stripeInfo?.clientSecret) {
          router.push({ name: 'stripe', state: { stripeInfo } })
          return
        }
      }

      const paymentUrl = findPaymentUrl(parsedPayExtra)
      if (paymentUrl) {
        window.location.assign(paymentUrl)
        return
      }

      sessionStorage.removeItem(VIP_GEM_PENDING_ORDER_KEY)
      toastError(`Unsupported payment action: ${action || 'UNKNOWN'}`)
    } catch (error) {
      sessionStorage.removeItem(VIP_GEM_PENDING_ORDER_KEY)
      toastError(error?.response?.data?.message ?? error?.message ?? t('recharge.errors.topUpFailed'))
    } finally {
      loading.value = false
      clickPayItem.value = null
    }
  }

  function guildGemTopUpClick() {
    if (!isGuildGemUser.value) return

    const payType = normalizePayType(
      select_recharge_item.value?.supportedPayType
      ?? supportedPayTypes.value[0]
    )
    if (!payType) {
      toastError(t('recharge.errors.topUpFailed'))
      return
    }
    return createVipGemOrder(payType)
  }

  async function stripePayClick() {
    if (activeRechargeType.value === 'gems') {
      return createVipGemOrder('STRIPE')
    }
    if (!user.value) {
      toastError(t('recharge.errors.enterUserToRecharge'))
      return 0;
    }

    if (clickPayItem.value) {
      toastError(t('recharge.errors.pleaseWait'))
      return;
    }

    try {
      clickPayItem.value = "STRIPE"

      var requestBody = {
        product_id: select_recharge_id.value,
        pay_item_type: "STRIPE",
        user_id: user.value.userId,
        v2: true,
        extra: JSON.stringify({
          return_url: window.location.origin + '/recharge'
        })
      }
      loading.value = true

      const rsp = await createOrder(requestBody)

      loading.value = false
      clickPayItem.value = null

      sessionStorage.setItem('__stp.od', JSON.stringify(rsp.data))
      sessionStorage.setItem('uid', user.value.userId)

      var stripeInfo = JSON.parse(rsp.data.payInfo.payInfoExtra.payExtra)

      router.push({ name: 'stripe', state: { stripeInfo } })
    } catch (error) {
      toastError(t('recharge.errors.topUpFailed'))
    } finally {
      loading.value = false
      clickPayItem.value = null
    }
  }

  // PayerMax 支付
  async function payermaxClick() {
    if (activeRechargeType.value === 'gems') {
      return createVipGemOrder(getGemPayType('PAYERMAX'))
    }
    if (!user.value) {
      toastError(t('recharge.errors.enterUserToRecharge'))
      return 0;
    }

    if (clickPayItem.value) {
      toastError(t('recharge.errors.pleaseWait'))
      return;
    }

    clickPayItem.value = "PAYERMAX"

    var requestBody = {
      product_id: select_recharge_id.value,
      pay_item_type: "PAYERMAX",
      user_id: user.value.userId,
      v2: true,
      extra: JSON.stringify({
        front_callback_url: window.location.origin + '/recharge'
      })
    }

    try {
      loading.value = true
      const rsp = await createOrder(requestBody)
      
      clickPayItem.value = null
      loading.value = false

      if (rsp.success) {
        var payermaxInfo = JSON.parse(rsp.data.payInfo.payInfoExtra.payExtra)
        window.location.href = payermaxInfo.redirectUrl
      } else {
        toastError(rsp.message ?? t('recharge.errors.createOrderFailed'))
      }
    } catch (e) {
      toastError(e)
    } finally {
      loading.value = false
      clickPayItem.value = null
    }
  }

  // DIANDIAN 支付
  async function diandianPayClick() {
    if (!user.value) {
      toastError(t('recharge.errors.enterUserToRecharge'))
      return 0;
    }

    if (clickPayItem.value) {
      toastError(t('recharge.errors.pleaseWait'))
      return;
    }

    clickPayItem.value = "DIANDIAN"

    var requestBody = {
      product_id: select_recharge_id.value,
      pay_item_type: "DIANDIAN",
      user_id: user.value.userId,
      v2: true,
      extra: JSON.stringify({
        callback_url: window.location.origin + '/recharge'
      })
    }

    try {
      loading.value = true
      const rsp = await createOrder(requestBody)
      
      clickPayItem.value = null
      loading.value = false

      if (rsp.success) {
        var payInfo = JSON.parse(rsp.data.payInfo.payInfoExtra.payExtra)
        window.location.href = payInfo.payLink
      } else {
        toastError(rsp.message ?? t('recharge.errors.createOrderFailed'))
      }
    } catch (e) {
      toastError(e)
    } finally {
      loading.value = false
      clickPayItem.value = null
    }
  }

  // PingPong 支付
  async function pingpongClick() {
    if (activeRechargeType.value === 'gems') {
      return createVipGemOrder(getGemPayType('PINGPONG'))
    }
    if (!user.value) {
      toastError(t('recharge.errors.enterUserToRecharge'))
      return 0;
    }

    if (clickPayItem.value) {
      toastError(t('recharge.errors.pleaseWait'))
      return;
    }

    const pingpongPayType = activeRechargeType.value === 'gems'
      ? normalizePayType(select_recharge_item.value?.supportedPayType)
      : 'PINGPONG'
    clickPayItem.value = pingpongPayType

    var requestBody = {
      product_id: select_recharge_id.value,
      pay_item_type: pingpongPayType,
      user_id: user.value.userId,
      v2: true,
      extra: JSON.stringify({
        front_callback_url: window.location.origin + '/recharge'
      })
    }

    try {
      loading.value = true
      const rsp = await createOrder(requestBody)
      
      clickPayItem.value = null
      loading.value = false

      console.log(rsp)
      if (rsp.success) {
        var pingpongInfo = JSON.parse(rsp.data.payInfo.payInfoExtra.payExtra)
        console.log('pingpongInfo', pingpongInfo)
        if(pingpongInfo && pingpongInfo.bizContent){
          var bizContent = JSON.parse(pingpongInfo.bizContent)
          if(bizContent && bizContent.paymentUrl){
            window.location.href = bizContent.paymentUrl
            return
          }
        }
        
        toastError(rsp.message ?? t('recharge.errors.createOrderFailed'))
      } else {
        toastError(rsp.message ?? t('recharge.errors.createOrderFailed'))
      }
    } catch (e) {
      toastError(e)
    } finally {
      loading.value = false
      clickPayItem.value = null
    }
  }

  // PayPal 支付
  async function customPaypalClick() {
    const paypalOrderInfo = await createPaypalOrder()
    if (paypalOrderInfo) {
      const link = paypalOrderInfo.links.find((item) => item.rel == 'payer-action')
      if (link) {
        window.location.href = link.href
      }
    }
  }

  async function createPaypalOrder() {
    try {
      if (!user.value) {
        toastError(t('recharge.errors.enterUserToRecharge'))
        return null;
      }


      var requestBody = {
        product_id: select_recharge_id.value,
        pay_item_type: "PAYPAL",
        user_id: user.value.userId,
        v2: true,
        extra: JSON.stringify({
          return_url: window.location.origin + '/recharge',
          cancel_url: window.location.origin + '/recharge'
        })
      }

      loading.value = true
      const orderRsp = await createOrder(requestBody)
      loading.value = false

      if (orderRsp.success) {
        let bizOrderId = orderRsp.data.orderId;
        var paypalOrderInfo = JSON.parse(orderRsp.data.payInfo.payInfoExtra.payExtra)
        orderMap[paypalOrderInfo.id] = orderRsp.data.orderId
        return paypalOrderInfo;
      } else {
        toastError(orderRsp.message ?? t('recharge.errors.createOrderFailed'))
        return null;
      }
    } catch (error) {
      console.error(error);
      toastError(t('recharge.errors.paypalOrderCreationFailed'))
      return null;
    } finally {
      loading.value = false
    }
  }

  // HAIPAY_USDT 支付
  async function haipayClick() {
    if (!user.value) {
      toastError(t('recharge.errors.enterUserToRecharge'))
      return 0;
    }

    if (clickPayItem.value) {
      toastError(t('recharge.errors.pleaseWait'))
      return;
    }

    clickPayItem.value = "HAIPAY_USDT"

    var requestBody = {
      product_id: select_recharge_id.value,
      pay_item_type: "HAIPAY_USDT",
      user_id: user.value.userId,
      v2: true,
      extra: JSON.stringify({
        callback_url: window.location.origin + '/recharge'
      })
    }

    try {
      loading.value = true
      const rsp = await createOrder(requestBody)
      
      clickPayItem.value = null
      loading.value = false

      if (rsp.success) {
        var haipayInfo = JSON.parse(rsp.data.payInfo.payInfoExtra.payExtra)
        window.location.href = haipayInfo.payUrl
      } else {
        toastError(rsp.message ?? t('recharge.errors.createOrderFailed'))
      }
    } catch (e) {
      toastError(e)
    } finally {
      loading.value = false
      clickPayItem.value = null
    }
  }

  async function handleRechargeReturn(query) {
    const rechargeType = callbackContext?.rechargeType
      || (query.gem_result == 1 ? 'gems' : 'coins')
    const request = rechargeReceiptRequest(query, callbackContext)
    const uid = request?.user_id || user_id.value
    loading.value = true
    try {
      if (!request) {
        showRechargeResult('failed', rechargeType, '', uid)
        return
      }
      const verified = await verifyRechargeOnce(resultStore, request, verifyReceipt, rechargeType)
      const result = rechargeDisplayResult(verified)
      showRechargeResult(result.status, rechargeType, result.message, uid)
    } finally {
      loading.value = false
      const pending = parseJsonValue(sessionStorage.getItem(VIP_GEM_PENDING_ORDER_KEY))
      if (request && `${pending?.orderId}` === `${request.order_id}`
        && `${pending?.userId}` === `${uid}`) {
        sessionStorage.removeItem(VIP_GEM_PENDING_ORDER_KEY)
      }
      const stripe = parseJsonValue(sessionStorage.getItem('__stp.od'))
      if (request && `${stripe?.orderId}` === `${request.order_id}`) {
        sessionStorage.removeItem('__stp.od')
      }
      await clearRechargeCallback(uid)
    }
  }

  onMounted(async () => {
    const query = normalizeCallbackQuery(router.currentRoute.value.query)
    const callback = RECEIPT_VERIFICATION_ENABLED && isRechargeCallback(query)
    callbackContext = callback ? resultStore.find(query) : null
    const queryUid = Array.isArray(query.uid) ? query.uid[0] : query.uid

    // 兼容没有追加 orderId 的 Gem 回跳，但不能让旧的 Gem 订单接管新的 Coin 回调。
    if (callback && !callbackContext && query.gem_result == 1
      && !query.orderId && !query.order_id) {
      const pending = parseJsonValue(sessionStorage.getItem(VIP_GEM_PENDING_ORDER_KEY))
      if (pending?.orderId && `${pending.userId}` === `${queryUid}`) {
        callbackContext = resultStore.get(pending.userId, pending.orderId)
          || { ...pending, rechargeType: 'gems' }
      }
    }

    // Stripe 旧版本兼容：必须匹配实际 Checkout session，不能复用任意旧订单。
    if (callback && !callbackContext && query.session_id) {
      const legacy = parseJsonValue(sessionStorage.getItem('__stp.od'))
      const payment = parseJsonValue(legacy?.payInfo?.payInfoExtra?.payExtra)
      const secret = payment?.clientSecret ?? payment?.client_secret ?? ''
      if (legacy?.orderId && secret.split('_secret_')[0] === query.session_id) {
        const context = parseJsonValue(sessionStorage.getItem(RECHARGE_RESULT_CONTEXT_KEY))
        const uid = sessionStorage.getItem('uid')
        callbackContext = context?.orderId === legacy.orderId && `${context.userId}` === `${uid}`
          ? context : { orderId: legacy.orderId, userId: uid, rechargeType: 'coins' }
      }
    }

    const rawUid = queryUid || callbackContext?.userId || ''
    user_id.value = `${rawUid}`.match(/^\d+/)?.[0] || ''
    activeRechargeType.value = callbackContext?.rechargeType
      || (query.gem_result == 1 ? 'gems' : 'coins')

    if (callback) {
      await handleRechargeReturn(query)
    } else {
      const restored = resultStore.getActive(user_id.value)
      if (restored) {
        rechargeResult.value = restored
        activeRechargeType.value = restored.rechargeType
      }
    }

    if (activeRechargeType.value === 'gems') {
      try {
        await ensureBaseGemProducts()
        applyGemProducts(gemBaseProducts.value)
        await validateGemUser()
      } catch (error) {
        if (!rechargeResult.value) toastError(error?.message || t('recharge.errors.topUpFailed'))
      }
    } else {
      const uid = user_id.value
      await getProductList()
      if (uid && uid === user_id.value) await getinfoByUid(uid)
    }
    // 无本地订单资料的旧回调仍可补充用户信息；数量保持未知，绝不使用默认档位。
    if (rechargeResult.value && `${user.value?.userId}` === rechargeResult.value.userId) {
      rechargeResult.value.avatar ||= user.value.avatar || ''
      rechargeResult.value.nickname ||= user.value.nickName || ''
      resultStore.setActive(rechargeResult.value)
    }
  })

  return {
    loading,
    select_recharge_id,
    select_recharge_item,
    paypal_product_item,
    recharge_list,
    supportedPayTypes,
    user,
    user_id,
    isNewUser,
    clickPayItem,
    rechargeResult,
    activeRechargeType,
    gemRechargeAllowed,
    rechargeOptionsLoading,
    showRechargeOptions,
    showPaymentOptions,
    isGuildGemUser,
    isPhilippineCoinUser,
    canSubmitPhilippineCoins,
    philippineCoinTopUpClick,
    isPaymentMethodAvailable,
    getPaymentDiscount,
    getPaymentPrice,
    getCoinOriginalPrice,
    getRechargeAmount,
    getRechargePrice,
    airwallexPayClick,
    priceChangeHandle,
    checkUid,
    saveUid,
    clearUserInput,
    closeRechargeResult,
    switchRechargeType,
    guildGemTopUpClick,
    stripePayClick,
    payermaxClick,
    diandianPayClick,
    pingpongClick,
    customPaypalClick,
    haipayClick,
  }
}
